import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Rocket, CheckCircle2, Loader2, ArrowRight, Sparkles } from 'lucide-react'
import { api } from '@/api/client'
import type { ProvisioningStatus, ProvisioningStep } from '@/api/types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'

interface Props {
  onComplete: () => Promise<void>
}

export function ProvisioningPage({ onComplete }: Props) {
  const navigate = useNavigate()
  const [status, setStatus] = useState<ProvisioningStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [provisioning, setProvisioning] = useState(false)
  const [done, setDone] = useState(false)
  const [orgName, setOrgName] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = useCallback(async () => {
    try {
      const s = await api.getProvisioningStatus()
      setStatus(s)
      if (s.provisioned) setDone(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch provisioning status')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const handleProvision = async () => {
    setProvisioning(true)
    setError(null)
    try {
      const result = await api.initialise({
        organisation_name: orgName || undefined,
        default_currency: currency || undefined,
      })
      setStatus(result)
      setDone(true)
      await onComplete()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Provisioning failed')
    } finally {
      setProvisioning(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner className="h-6 w-6 text-primary" />
      </div>
    )
  }

  // Already provisioned success state
  if (done && status?.provisioned) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mx-auto max-w-lg py-16 text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
          className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-ready/10"
        >
          <CheckCircle2 className="h-10 w-10 text-ready" />
        </motion.div>
        <h1 className="text-2xl tracking-tight"><span className="font-bold">Ledger.</span> <span className="font-light text-muted-foreground text-xl">Core</span> is Ready</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          All systems provisioned and operational. Your financial infrastructure is live.
        </p>

        {status.steps && (
          <div className="mt-8 space-y-2 text-left">
            {status.steps.map((step: ProvisioningStep) => (
              <div key={step.name} className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
                <CheckCircle2 className="h-4 w-4 text-ready" />
                <span className="flex-1 text-sm">{step.name}</span>
                <Badge variant="success" className="text-[10px]">
                  {step.status}
                </Badge>
              </div>
            ))}
          </div>
        )}

        <Button className="mt-8" onClick={() => navigate('/')}>
          Go to Dashboard
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </motion.div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      {/* Hero */}
      <div className="text-center">
        <motion.div
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10"
        >
          <Rocket className="h-8 w-8 text-primary" />
        </motion.div>
        <h1 className="text-2xl tracking-tight">Provision <span className="font-bold">Ledger.</span> <span className="font-light text-muted-foreground text-xl">Core</span></h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Zero-configuration setup. Ledger. Core will generate its cryptographic identity,
          create your organisation, and establish foundational accounts.
        </p>
      </div>

      {/* What will happen */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            What happens during provisioning
          </CardTitle>
          <CardDescription>Three idempotent steps, safe to run multiple times</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            {
              title: 'Generate System Identity',
              description: 'An Ed25519 keypair is created and the private key encrypted at rest. This identity signs all financial transactions.',
            },
            {
              title: 'Create Organisation',
              description: 'Your organisation boundary is established. All accounts and transactions exist within it.',
            },
            {
              title: 'Create Foundational Accounts',
              description: 'Eight structural accounts (Assets, Liabilities, Equity, Revenue, Expenses, Retained Earnings, Suspense, Opening Balance) are created with enforced balance policies.',
            },
          ].map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="flex gap-4"
            >
              <div className="flex flex-col items-center">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-primary/30 bg-primary/5 text-sm font-semibold text-primary">
                  {i + 1}
                </div>
                {i < 2 && <div className="mt-1 h-full w-px bg-border" />}
              </div>
              <div className="pb-6">
                <h4 className="text-sm font-medium">{step.title}</h4>
                <p className="mt-0.5 text-xs text-muted-foreground">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </CardContent>
      </Card>

      {/* Optional configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configuration (optional)</CardTitle>
          <CardDescription>Sensible defaults are used if left empty</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="orgName">Organisation Name</Label>
            <Input
              id="orgName"
              placeholder="My Company"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Defaults to &quot;Default Organisation&quot; if not specified
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">Default Currency</Label>
            <Input
              id="currency"
              placeholder="USD"
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              maxLength={3}
              className="w-24 uppercase"
            />
            <p className="text-xs text-muted-foreground">ISO 4217 code (e.g. USD, EUR, GBP)</p>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </motion.div>
      )}

      {/* Actions */}
      <div className="flex justify-center pb-8">
        <Button size="lg" onClick={handleProvision} disabled={provisioning}>
          {provisioning ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Provisioning…
            </>
          ) : (
            <>
              <Rocket className="mr-2 h-4 w-4" />
              Initialise Ledger. Core
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
